import { useEffect, useState } from 'react';
import { useAuth } from '../auth';
import { useNavigate } from 'react-router-dom';
import './CoachModal.css';

const API = import.meta.env.VITE_API_URL || 'http://localhost:8000';

interface CoachStep {
  row: number;
  col: number;
  value: number;
  technique: string;
  reason: string;
  step_index: number;
}

interface CoachResponse {
  solved: boolean;
  technique_counts: Record<string, number>;
  steps: CoachStep[];
  summary: string;
}

interface Props {
  open: boolean;
  initialBoard: number[][];
  onClose: () => void;
  t: any;
}

export function CoachModal({ open, initialBoard, onClose, t }: Props) {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<CoachResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isPremium = !!user?.is_premium;

  useEffect(() => {
    if (!open || !isPremium) return;
    setLoading(true);
    setError(null);
    fetch(`${API}/coach/analyze`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ board: initialBoard }),
    })
      .then(async (r) => {
        if (!r.ok) {
          const d = await r.json().catch(() => ({}));
          throw new Error(d?.detail ?? t.common.error);
        }
        return r.json();
      })
      .then(setData)
      .catch((e) => setError(String(e?.message ?? e)))
      .finally(() => setLoading(false));
  }, [open, isPremium, token, initialBoard]);

  if (!open) return null;

  return (
    <div className="coach-modal-backdrop" onClick={onClose}>
      <div className="coach-modal" onClick={(e) => e.stopPropagation()}>
        <header className="coach-modal-header">
          <span className="coach-icon">🧠</span>
          <h2>{t.coach.title}</h2>
          <button className="coach-close" onClick={onClose} aria-label={t.common.close}>×</button>
        </header>

        {!isPremium ? (
          <div className="coach-locked">
            <div className="coach-lock-icon">🔒</div>
            <h3>{t.coach.premiumOnly}</h3>
            <p>{t.coach.premiumDesc}</p>
            <button className="coach-cta" onClick={() => { onClose(); navigate('/premium'); }}>
              {t.coach.upgradeBtn}
            </button>
          </div>
        ) : loading ? (
          <div className="coach-loading">
            <div className="coach-spinner" />
            <p>{t.coach.analyzing}</p>
          </div>
        ) : error ? (
          <div className="coach-error">{error}</div>
        ) : data ? (
          <div className="coach-body">
            <p className="coach-summary">{data.summary}</p>

            <h4>{t.coach.techniqueCounts}</h4>
            <ul className="coach-tech-list">
              {Object.entries(data.technique_counts).sort((a, b) => b[1] - a[1]).map(([name, count]) => (
                <li key={name}>
                  <span className="coach-tech-name">{name}</span>
                  <span className="coach-tech-count">×{count}</span>
                </li>
              ))}
            </ul>

            <h4>{t.coach.stepsTitle}</h4>
            <ol className="coach-step-list">
              {data.steps.slice(0, 30).map((s) => (
                <li key={s.step_index}>
                  <span className="coach-step-cell">
                    R{s.row + 1}C{s.col + 1} = {s.value}
                  </span>
                  <span className="coach-step-tech">{s.technique}</span>
                  <span className="coach-step-reason">{s.reason}</span>
                </li>
              ))}
              {data.steps.length > 30 && (
                <li className="coach-more">
                  …{t.coach?.moreSteps?.replace('{n}', String(data.steps.length - 30)) ?? `+${data.steps.length - 30} adım daha`}
                </li>
              )}
            </ol>
          </div>
        ) : null}
      </div>
    </div>
  );
}
